<template>
    <div>
        <Navbar/>
        
        <!-- Breadcrumb Area -->
        <div class="breadcroumb-area gray-bg-2">
            <div class="container">
                <div class="row">
                    <div class="col-lg-12">
                        <div class="breadcroumb-title">
                            <h1 class="text-dark">{{ currentNews.title }}</h1>
                            <div class="blog-meta">
                                <p class="text-gray-2">{{ formatDate(currentNews.date) }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- News Details -->
        <div class="single-blog-wrapper section-padding">
            <div class="container">
                <div class="row justify-content-center">
                    <div class="col-xl-10">
                        <!-- デバッグ情報（開発時のみ表示） -->
                        <div v-if="false" class="debug-info" style="background: #f0f0f0; padding: 20px; margin-bottom: 20px;">
                            <h4>デバッグ情報</h4>
                            <pre>{{ JSON.stringify(currentNews, null, 2) }}</pre>
                            <p>画像配列: {{ allImages }}</p>
                        </div>
                        
                        <!-- ローディング表示 -->
                        <div v-if="loading" class="text-center">
                            <p>ニュースを読み込んでいます...</p>
                        </div>
                        
                        <!-- ニュース本文 -->
                        <div v-else-if="currentNews.id" class="blog-content-wrap">
                            <!-- メイン画像 -->
                            <div v-if="allImages.length > 0 && allImages[0]" class="blog-thumb mb-4">
                                <img :src="allImages[0]" :alt="currentNews.title" @error="handleMainImageError">
                            </div>
                            
                            <!-- 本文 -->
                            <div class="news-content-body">
                                <p class="text-gray" v-html="formatContent(currentNews.content)"></p>
                            </div>
                            
                            <!-- 追加画像ギャラリー -->
                            <div v-if="additionalImages.length > 0" class="additional-images mt-5">
                                <h4 class="mb-3">関連画像</h4>
                                <div class="row">
                                    <div 
                                        v-for="(image, index) in additionalImages" 
                                        :key="index"
                                        class="col-md-4 col-6 mb-3"
                                    >
                                        <div class="gallery-item" @click="openLightbox(image)">
                                            <img :src="image" :alt="`画像 ${index + 2}`" @error="handleImageError">
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <!-- 戻るボタン -->
                            <div class="back-link mt-5">
                                <router-link to="/" class="main-btn">トップページに戻る</router-link>
                                <router-link to="/news" class="main-btn ms-2" style="background: #6c757d;">ニュース一覧</router-link>
                            </div>
                        </div>
                        
                        <!-- ニュースが見つからない場合 -->
                        <div v-else class="text-center">
                            <p>ニュースが見つかりませんでした。</p>
                            <router-link to="/" class="main-btn mt-3">トップページに戻る</router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Lightbox Modal -->
        <div v-if="lightboxImage" class="lightbox-modal" @click="closeLightbox">
            <div class="lightbox-content">
                <img :src="lightboxImage" alt="拡大画像">
                <button class="lightbox-close" @click="closeLightbox">&times;</button>
            </div>
        </div>
        
        <Footer/>
    </div>
</template>

<script>
import Navbar from '@/components/Navbar.vue'
import Footer from '@/components/Footer.vue'

export default {
    name: 'NewsDetail',
    components: {
        Navbar, Footer
    },
    data() {
        return {
            currentNews: {},
            allNews: [],
            loading: true,
            SHEET_ID: process.env.VUE_APP_GOOGLE_SHEET_ID || '',
            lightboxImage: null,
            allImages: [],
            additionalImages: []
        }
    },
    async mounted() {
        window.scrollTo(0, 0);
        await this.fetchNewsData();
    },
    methods: {
        async fetchNewsData() {
            try {
                this.loading = true;
                
                if (!this.SHEET_ID) {
                    console.log('Google Sheet IDが設定されていません');
                    await this.loadFromJSON();
                    return;
                }
                
                // キャッシュ回避のためタイムスタンプを追加
                const CSV_URL = `https://docs.google.com/spreadsheets/d/${this.SHEET_ID}/export?format=csv&gid=0&t=${Date.now()}`;
                
                try {
                    const response = await fetch(CSV_URL);
                    const csvText = await response.text();
                    this.allNews = this.parseCSV(csvText);
                } catch (corsError) {
                    console.log('CORS error, trying proxy...');
                    await this.fetchWithProxy();
                }
                
                const newsId = this.$route.params.id;
                this.currentNews = this.allNews.find(news => news.id === newsId) || {};
                
                console.log('Found news item:', this.currentNews);
                
                // 画像を処理
                this.processImages();
                    
            } catch (error) {
                console.error('ニュース取得エラー:', error);
                await this.loadFromJSON();
            } finally {
                this.loading = false;
            }
        },
        
        async fetchWithProxy() {
            try {
                // キャッシュ回避のためタイムスタンプを追加
                const CSV_URL = `https://docs.google.com/spreadsheets/d/${this.SHEET_ID}/export?format=csv&gid=0&t=${Date.now()}`;
                const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(CSV_URL)}`;
                
                const response = await fetch(proxyUrl);
                const data = await response.json();
                this.allNews = this.parseCSV(data.contents);
            } catch (error) {
                console.error('プロキシ経由の取得もエラー:', error);
            }
        },
        
        async loadFromJSON() {
            try {
                const response = await fetch('/assets/data/blogs.json');
                const data = await response.json();
                this.allNews = data.blogs;
                
                const newsId = this.$route.params.id;
                this.currentNews = this.allNews.find(news => news.id == newsId) || {};
                this.processImages();
            } catch (error) {
                console.error('JSONからの読み込みエラー:', error);
            }
        },
        
        processImages() {
            const images = [];
            
            console.log('Processing images for:', this.currentNews);
            
            // image1〜image5をチェック
            for (let i = 1; i <= 5; i++) {
                const imageKey = `image${i}`;
                const imageValue = this.currentNews[imageKey];
                
                if (imageValue && imageValue.trim() && imageValue.trim() !== '') {
                    images.push(imageValue.trim());
                }
            }
            
            // imageUrl もチェック（互換性のため）
            if (this.currentNews.imageurl && this.currentNews.imageurl.trim()) {
                if (!images.includes(this.currentNews.imageurl.trim())) {
                    images.unshift(this.currentNews.imageurl.trim());
                }
            }
            
            // image もチェック（既存データ互換性のため）
            if (this.currentNews.image && this.currentNews.image.trim()) {
                if (!images.includes(this.currentNews.image.trim())) {
                    images.unshift(this.currentNews.image.trim());
                }
            }
            
            console.log('Processed images:', images);
            
            this.allImages = images;
            this.additionalImages = images.slice(1);
        },
        
        // CSV解析（改行を含むフィールドに対応）
        parseCSV(csvText) {
            const rows = this.parseCSVToRows(csvText);
            
            if (rows.length < 2) return [];
            
            const headers = rows[0].map(h => h.toLowerCase().trim());
            const newsItems = [];
            
            for (let i = 1; i < rows.length; i++) {
                const values = rows[i];
                const item = {};
                
                headers.forEach((header, index) => {
                    item[header] = values[index] || '';
                });
                
                // idとtitleが存在するか確認
                if (!item.id || !item.title) {
                    continue;
                }
                
                // published判定を改善（大文字・小文字・日本語対応）
                const publishedValue = (item.published || '').toString().toUpperCase().trim();
                if (publishedValue === 'TRUE' || publishedValue === '公開' || publishedValue === 'YES' || publishedValue === '1') {
                    newsItems.push(item);
                }
            }
            
            return newsItems.sort((a, b) => new Date(b.date) - new Date(a.date));
        },
        
        // CSVテキストを行の配列に変換（改行を含むフィールドに対応）
        parseCSVToRows(csvText) {
            const rows = [];
            let currentRow = [];
            let currentField = '';
            let inQuotes = false;
            
            for (let i = 0; i < csvText.length; i++) {
                const char = csvText[i];
                const nextChar = csvText[i + 1];
                
                if (inQuotes) {
                    if (char === '"') {
                        if (nextChar === '"') {
                            // エスケープされたダブルクォート
                            currentField += '"';
                            i++; // 次の文字をスキップ
                        } else {
                            // クォート終了
                            inQuotes = false;
                        }
                    } else {
                        // クォート内の文字（改行含む）
                        currentField += char;
                    }
                } else {
                    if (char === '"') {
                        // クォート開始
                        inQuotes = true;
                    } else if (char === ',') {
                        // フィールド区切り
                        currentRow.push(currentField.trim());
                        currentField = '';
                    } else if (char === '\n' || (char === '\r' && nextChar === '\n')) {
                        // 行終了
                        currentRow.push(currentField.trim());
                        if (currentRow.some(field => field !== '')) {
                            rows.push(currentRow);
                        }
                        currentRow = [];
                        currentField = '';
                        if (char === '\r') {
                            i++; // \nをスキップ
                        }
                    } else if (char === '\r') {
                        // 単独の\r（古いMac形式）
                        currentRow.push(currentField.trim());
                        if (currentRow.some(field => field !== '')) {
                            rows.push(currentRow);
                        }
                        currentRow = [];
                        currentField = '';
                    } else {
                        currentField += char;
                    }
                }
            }
            
            // 最後のフィールドと行を追加
            if (currentField || currentRow.length > 0) {
                currentRow.push(currentField.trim());
                if (currentRow.some(field => field !== '')) {
                    rows.push(currentRow);
                }
            }
            
            return rows;
        },
        
        formatDate(date) {
            if (!date) return '';
            try {
                return new Date(date).toLocaleDateString('ja-JP', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            } catch (e) {
                return date;
            }
        },
        
        formatContent(content) {
            if (!content) return '';
            // 改行をbrタグに変換
            return content.replace(/\n/g, '<br>');
        },
        
        handleMainImageError(event) {
            event.target.src = 'https://www.gharb.tokyo/assets/img/blog/news1.png';
        },
        
        handleImageError(event) {
            event.target.style.display = 'none';
        },
        
        openLightbox(image) {
            this.lightboxImage = image;
            document.body.style.overflow = 'hidden';
        },
        
        closeLightbox() {
            this.lightboxImage = null;
            document.body.style.overflow = '';
        }
    }
}
</script>

<style scoped>
.blog-content-wrap {
    background: #fff;
    padding: 30px;
    border-radius: 10px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.blog-thumb {
    border-radius: 10px;
    overflow: hidden;
}

.blog-thumb img {
    width: 100%;
    height: auto;
    max-height: 500px;
    object-fit: cover;
}

.news-content-body {
    font-size: 16px;
    line-height: 1.8;
    color: #333;
}

.additional-images h4 {
    color: #333;
    font-weight: 600;
}

.gallery-item {
    cursor: pointer;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 5px rgba(0,0,0,0.1);
    transition: transform 0.3s;
}

.gallery-item:hover {
    transform: scale(1.02);
}

.gallery-item img {
    width: 100%;
    height: 150px;
    object-fit: cover;
}

.back-link {
    padding-top: 20px;
    border-top: 1px solid #eee;
}

.main-btn {
    display: inline-block;
    padding: 12px 30px;
    background: #7c5739;
    color: #fff;
    text-decoration: none;
    border-radius: 5px;
    transition: background 0.3s;
}

.main-btn:hover {
    background: #5a3f28;
    color: #fff;
}

/* Lightbox */
.lightbox-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0,0,0,0.9);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    cursor: pointer;
}

.lightbox-content {
    position: relative;
    max-width: 90%;
    max-height: 90%;
}

.lightbox-content img {
    max-width: 100%;
    max-height: 90vh;
    object-fit: contain;
}

.lightbox-close {
    position: absolute;
    top: -40px;
    right: 0;
    background: none;
    border: none;
    color: #fff;
    font-size: 40px;
    cursor: pointer;
    line-height: 1;
}

.lightbox-close:hover {
    color: #ccc;
}
</style>